/** Adapter between the API quote/invoice payloads (raw Prisma) and the UI types. */

import type {
  DocLine,
  Invoice,
  InvoicePaymentRecord,
  InvoiceStatus,
  Quote,
  QuoteStatus,
} from "@/types";

interface ApiLine {
  id: string;
  serviceId?: string | null;
  label: string;
  qty: number | string;
  unitPrice: number | string;
  lineTotal: number | string;
}

export interface ApiQuote {
  id: string;
  number: string;
  clientId: string;
  status: QuoteStatus;
  subtotal: number | string;
  discount: number | string;
  vatApplied: boolean;
  vatAmount: number | string;
  total: number | string;
  validityDays: number;
  createdAt: string;
  client?: { id: string; name: string } | null;
  lines?: ApiLine[];
}

export interface ApiInvoice {
  id: string;
  number: string;
  clientId: string;
  status: InvoiceStatus;
  subtotal: number | string;
  discount: number | string;
  vatApplied: boolean;
  vatAmount: number | string;
  total: number | string;
  paidAmount: number | string;
  dueDate?: string | null;
  createdAt: string;
  client?: { id: string; name: string } | null;
  lines?: ApiLine[];
  payments?: Array<{
    id: string;
    amount: number | string;
    method: string;
    date: string;
    account?: { id: string; name: string } | null;
  }>;
}

export interface DocLineInput {
  serviceId?: string;
  label: string;
  qty: number;
  unitPrice: number;
}

export interface QuoteInput {
  clientId: string;
  lines: DocLineInput[];
  discount: number;
  vatApplied: boolean;
  validityDays?: number;
  terms?: string;
}

function toNumber(value: number | string | null | undefined): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === "string" ? Number(value) : value;
  return Number.isFinite(n) ? n : 0;
}

function formatDate(raw: string | null | undefined): string {
  if (!raw) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
}

function toLine(api: ApiLine): DocLine {
  return {
    id: api.id,
    serviceId: api.serviceId ?? undefined,
    label: api.label,
    qty: toNumber(api.qty),
    unitPrice: toNumber(api.unitPrice),
    lineTotal: toNumber(api.lineTotal),
  };
}

export function toQuote(api: ApiQuote): Quote {
  return {
    id: api.id,
    number: api.number,
    clientId: api.clientId,
    clientName: api.client?.name ?? "—",
    status: api.status,
    subtotal: toNumber(api.subtotal),
    discount: toNumber(api.discount),
    vatApplied: Boolean(api.vatApplied),
    vatAmount: toNumber(api.vatAmount),
    total: toNumber(api.total),
    validityDays: api.validityDays,
    date: formatDate(api.createdAt),
    lines: (api.lines ?? []).map(toLine),
  };
}

export function toInvoice(api: ApiInvoice): Invoice {
  const payments: InvoicePaymentRecord[] = (api.payments ?? []).map((p) => ({
    id: p.id,
    amount: toNumber(p.amount),
    method: p.method,
    date: formatDate(p.date),
    accountName: p.account?.name ?? "—",
  }));
  return {
    id: api.id,
    number: api.number,
    clientId: api.clientId,
    clientName: api.client?.name ?? "—",
    status: api.status,
    subtotal: toNumber(api.subtotal),
    discount: toNumber(api.discount),
    vatApplied: Boolean(api.vatApplied),
    vatAmount: toNumber(api.vatAmount),
    total: toNumber(api.total),
    paidAmount: toNumber(api.paidAmount),
    dueDate: api.dueDate ? formatDate(api.dueDate) : undefined,
    date: formatDate(api.createdAt),
    lines: (api.lines ?? []).map(toLine),
    payments,
  };
}

/** Client-side preview of the server money math (VAT 19%). */
export function previewTotals(lines: DocLineInput[], discount: number, vatApplied: boolean) {
  const round = (v: number) => Math.round(v * 100) / 100;
  const subtotal = round(lines.reduce((s, l) => s + l.qty * l.unitPrice, 0));
  const cappedDiscount = Math.min(discount, subtotal);
  const base = round(subtotal - cappedDiscount);
  const vatAmount = vatApplied ? round(base * 0.19) : 0;
  return { subtotal, discount: cappedDiscount, vatAmount, total: round(base + vatAmount) };
}
